import { apiDownload, apiFetch, setTokens, clearTokens } from './client'
import type {
  AuthUser,
  FieldDefinition,
  GenerationModel,
  GenerationTask,
  GenerationTaskList,
  GenerationRow,
  LoginResponse,
  ApiProvider,
  AdminStats,
  StorageOverview,
  ExternalValidatorConfig,
  LogListResponse,
  OutputFormat,
  ValidatorServicesResponse,
} from './types'

/** --- Auth ---------------------------------------------------------------- */

export const authApi = {
  login: (identifier: string, password: string) =>
    apiFetch<LoginResponse>('POST', '/api/login/', {
      body: { identifier, password },
      anonymous: true,
    }),
  register: (phone: string, password: string) =>
    apiFetch<AuthUser>('POST', '/api/register/', {
      body: { phone, password },
      anonymous: true,
    }),
  me: () => apiFetch<AuthUser>('GET', '/api/me/'),
  logoutLocal: () => clearTokens(),
  saveTokens: (access: string, refresh: string) => setTokens(access, refresh),
}

/** --- Models (read-only) -------------------------------------------------- */

export const modelsApi = {
  list: () => apiFetch<GenerationModel[]>('GET', '/api/models/'),
}

/** --- Tasks --------------------------------------------------------------- */

export interface CreateTaskBody {
  name: string
  output_format?: OutputFormat
  language?: string
  defaults?: Record<string, unknown>
  common_prompt?: string
  temperature?: number | null
  max_tokens?: number | null
  text_ru_enabled?: boolean
  max_regeneration_attempts?: number
}

export interface UploadResponse {
  headers: string[]
  row_count: number
  preflight: {
    rows_total: number
    rows_skipped: number
    duplicates: number[]
    warnings: string[]
  }
  preview: Record<string, unknown>[]
}

export interface MappingResponse {
  mapping: Record<string, string>
  rows: { created: number; skipped: number; total: number }
}

export interface ListRowsResponse {
  count: number
  total_rows: number
  page: number
  page_size: 10 | 25 | 50 | 100
  total_pages: number
  status_counts: Record<string, number>
  completed_rows: number
  progress: number
  results: Array<{
    id: string
    source_row_number: number
    status: string
    model_alias: string
    attempts_count: number
    final_result: string
    cleaned_result: string
    final_prompt: string
  }>
}

export interface PreparationIssue {
  code: string
  message: string
  count: number
  rows: number[]
  column?: string
  field?: string
  samples?: string[]
  variables?: string[]
}

export interface TaskPreparationReport {
  can_start: boolean
  summary: {
    total_rows: number
    skipped_rows: number
    individual_prompts: number
    common_prompts: number
    missing_prompts: number
  }
  individual_prompt_column: boolean
  allow_common_prompt_fallback: boolean
  errors: PreparationIssue[]
  warnings: PreparationIssue[]
  examples: Array<{
    row_id: string
    row_number: number
    mode: 'row_prompt' | 'common_template' | 'error'
    error_code: string
    unresolved_vars: string[]
    prompt: string
    truncated: boolean
  }>
}

export interface StartTaskResponse {
  task_status: string
  status: string
  message?: string
}

export const tasksApi = {
  list: () => apiFetch<GenerationTaskList[]>('GET', '/api/tasks/'),
  retrieve: (id: string) => apiFetch<GenerationTask>('GET', '/api/tasks/{id}/', { params: { id } }),
  create: (body: CreateTaskBody) => apiFetch<GenerationTask>('POST', '/api/tasks/', { body }),
  update: (id: string, body: Partial<CreateTaskBody>) =>
    apiFetch<GenerationTask>('PATCH', '/api/tasks/{id}/', { body, params: { id } }),
  remove: (id: string) => apiFetch<void>('DELETE', '/api/tasks/{id}/', { params: { id } }),
  upload: (id: string, file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return apiFetch<UploadResponse>('POST', '/api/tasks/{id}/upload/', { formData: fd, params: { id } })
  },
  preview: (id: string) =>
    apiFetch<{
      headers: string[]
      rows: unknown[]
      warnings: string[]
      auto_mapping: Record<string, string>
      target_fields: string[]
      field_definitions: FieldDefinition[]
    }>('GET', '/api/tasks/{id}/preview/', { params: { id } }),
  mapping: (id: string, mapping?: Record<string, string>) =>
    apiFetch<MappingResponse>('POST', '/api/tasks/{id}/mapping/', { body: { mapping }, params: { id } }),
  preparation: (id: string) =>
    apiFetch<TaskPreparationReport>('GET', '/api/tasks/{id}/preparation/', { params: { id } }),
  start: (id: string) => apiFetch<StartTaskResponse>('POST', '/api/tasks/{id}/start/', { params: { id } }),
  pause: (id: string) => apiFetch<{ status: string }>('POST', '/api/tasks/{id}/pause/', { params: { id } }),
  resume: (id: string) => apiFetch<StartTaskResponse>('POST', '/api/tasks/{id}/resume/', { params: { id } }),
  cancel: (id: string) => apiFetch<{ status: string }>('POST', '/api/tasks/{id}/cancel/', { params: { id } }),
  restart: (id: string) => apiFetch<StartTaskResponse>('POST', '/api/tasks/{id}/restart/', { params: { id } }),
  restartFailed: (id: string) =>
    apiFetch<{ task_status: string; dispatched: number }>('POST', '/api/tasks/{id}/restart-failed/', { params: { id } }),
  qualityRecheckStatus: (id: string) =>
    apiFetch<{ run: QualityRecheckRunInfo | null }>('GET', '/api/tasks/{id}/quality-recheck/', { params: { id } }),
  runQualityRecheck: (id: string) =>
    apiFetch<{ run: QualityRecheckRunInfo }>('POST', '/api/tasks/{id}/quality-recheck/', { params: { id } }),
  rows: (id: string, options?: { status?: string; page?: number; page_size?: 10 | 25 | 50 | 100 }) =>
    apiFetch<ListRowsResponse>('POST', '/api/tasks/{id}/rows/', {
      body: options ?? {},
      params: { id },
    }),
  export: (id: string, format: 'excel' | 'csv' = 'excel', intermediate = false) =>
    apiFetch<{ id: string; name: string; format: string; row_count: number; file_url: string | null }>(
      'POST', '/api/tasks/{id}/export/', { body: { format, intermediate }, params: { id } },
    ),
  downloadExport: (url: string) => apiDownload(url),
  checksConfig: (id: string) =>
    apiFetch<ChecksConfigResponse>('GET', '/api/tasks/{id}/checks-config/', { params: { id } }),
  updateChecksConfig: (id: string, config: ChecksConfig) =>
    apiFetch<{ status: string }>('PATCH', '/api/tasks/{id}/checks-config/', { body: config, params: { id } }),
}

/** --- Checks config (per-task) ------------------------------------------- */

export interface KeywordRule {
  phrase: string
  min: number
  max?: number
}

export type CheckMode = 'from_file' | 'override'

export interface QualityRecheckRunInfo {
  id: string
  status: 'queued' | 'running' | 'completed' | 'completed_with_errors'
  total_rows: number
  dispatched_rows: number
  processed_rows: number
  skipped_rows: number
  quality_failed_rows: number
  service_error_rows: number
  progress: number
  created_at: string
  started_at: string | null
  finished_at: string | null
}

export interface ChecksConfig {
  target_length?: number | null
  length_tolerance?: number | null
  spam_limit?: number | null
  exact_entries?: KeywordRule[]
  word_forms?: KeywordRule[]
  keyword_rules?: KeywordRule[]
  forbidden_words?: string[]
  exact_keywords?: string
  word_forms_keywords?: string
  checks?: Record<string, boolean | string>
  // Mode fields (stored in current_config, not in checks)
  length_mode?: CheckMode
  length_tolerance_mode?: CheckMode
  spam_limit_mode?: CheckMode
  exact_entries_mode?: CheckMode
  word_forms_mode?: CheckMode
  forbidden_words_mode?: CheckMode
}

export interface ExternalValidatorInfo {
  id: string
  validator_type: string
  threshold: number
  is_active: boolean
  task_enabled: boolean
}

export interface ChecksConfigResponse {
  auto_detected: Record<string, Array<{ row: number; value: unknown }>>
  current_config: ChecksConfig
  external_validators: ExternalValidatorInfo[]
  active_spam_formula: { version: string; name: string } | null
}

/** --- Spam formula (admin) ----------------------------------------------- */

export interface SpamFormulaVersion {
  id: string
  version_number: number
  expression: string
  created_by: string | null
  created_at: string
}

export interface SpamFormula {
  id: string
  name: string
  version: string
  version_number: number
  formula_type: 'structured_v6' | 'custom_expression'
  expression: string
  threshold_overrides: Record<string, unknown>
  extra_stopwords: string[]
  description: string
  is_active: boolean
  versions: SpamFormulaVersion[]
  created_at: string
  updated_at: string
}

export interface NauseaReport {
  overall_nausea_percent: number
  leader_unit: string
  leader_type: string
  leader_occurrences: number
  leader_word_length: number
  word_nausea: Record<string, unknown>
  academic_nausea: Record<string, unknown>
  phrase_nausea: Record<string, unknown>
  keyword_nausea: Record<string, unknown>[]
  total_words: number
  content_words: number
  status: string
  reasons: string[]
}

export interface SpamFormulaTestResult {
  report?: NauseaReport
  error: string | null
  score?: number
}

/** --- System settings (admin general settings singleton) ------------------ */

export interface SystemSettings {
  default_timeout_sec: number
  default_retry_count: number
  image_csv_max_mib: number
  image_task_json_max_mib: number
  image_prompt_max_chars: number
  image_group_name_max_chars: number
  image_generation_max_images: number
  image_retry_count: number
  image_retry_statuses: number[]
  default_max_regeneration_attempts: number
  default_temperature: number | null
  default_max_tokens: number | null
  request_delay_min_sec: number
  request_delay_max_sec: number
  rate_limit_buffer: number
  quality_checks_concurrency: number
  mock_mode: boolean
  updated_at: string
}

export type SystemSettingsBody = Partial<Omit<SystemSettings, 'updated_at'>>

/** --- Rows ---------------------------------------------------------------- */

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface CheckDetail {
  code: string
  status: string
  message: string
  expected: Record<string, unknown> | null
  found: Record<string, unknown> | null
  details: Record<string, unknown> | null
}

export interface AttemptDetail {
  id: string
  attempt_no: number
  status: string
  model_alias: string
  error_code: string
  error_message: string
  raw_response: string
  cleaned_result: string
  edited_result: string
  usage_input_tokens: number | null
  usage_output_tokens: number | null
  cached_input_tokens: number | null
  cost_from_response: string | null
  cost_estimate: string | null
  cost_currency: string
  started_at: string | null
  finished_at: string | null
  conversation_history: ChatMessage[]
  checks: CheckDetail[]
}

export interface RowDetail extends GenerationRow {
  attempts: AttemptDetail[]
  primary_attempt_id: string | null
  checks: CheckDetail[]
}

export const rowsApi = {
  retrieve: (id: string) => apiFetch<RowDetail>('GET', '/api/rows/{id}/', { params: { id } }),
  update: (id: string, body: { final_result?: string; reason?: string; settings_payload?: Record<string, unknown> }) =>
    apiFetch<GenerationRow>('PATCH', '/api/rows/{id}/', { body, params: { id } }),
  recheck: (id: string) => apiFetch<{ checks: RowDetail['checks'] }>(
    'POST', '/api/rows/{id}/recheck/', { params: { id } },
  ),
  regenerate: (id: string) =>
    apiFetch<{ status: string; row_id: string }>('POST', '/api/rows/{id}/regenerate/', { params: { id } }),
  setPrimary: (id: string, attemptId: string) =>
    apiFetch<{ primary_attempt_id: string; final_result: string; status: string }>(
      'POST', '/api/rows/{id}/set-primary/', { body: { attempt_id: attemptId }, params: { id } },
    ),
  editAttempt: (id: string, attemptId: string, text: string, reason?: string) =>
    apiFetch<{ attempt_id: string; edited_result: string; final_result: string; status: string }>(
      'POST', '/api/rows/{id}/edit-attempt/', { body: { attempt_id: attemptId, text, reason: reason || '' }, params: { id } },
    ),
}

/** --- Admin --------------------------------------------------------------- */

export interface AdminProvider extends ApiProvider {
  max_image_references: number
  supports_models_endpoint: boolean
  cost_from_response: boolean
  cache_hit_from_response: boolean
  is_local: boolean
  is_default: boolean
  api_type: 'openai' | 'anthropic'
  api_key_last4: string
}

export interface AdminModel {
  id: string
  alias: string
  model_name: string
  display_name: string
  provider: string
  provider_slug: string
  is_active: boolean
  is_custom: boolean
  supports_image_editing: boolean
  supports_image_generation: boolean
  supports_reference_generation: boolean
  description: string
  max_input_tokens: number | null
  max_output_tokens: number | null
  default_temperature: number | null
  cost_input_token: number | null
  cost_output_token: number | null
  created_at: string
  updated_at: string
}

export interface AdminModelBody {
  supports_image_editing?: boolean
  supports_image_generation?: boolean
  supports_reference_generation?: boolean
  alias?: string
  model_name?: string
  display_name?: string
  provider?: string
  is_active?: boolean
  description?: string
  max_input_tokens?: number | null
  max_output_tokens?: number | null
  default_temperature?: number | null
  cost_input_token?: number | null
  cost_output_token?: number | null
}

export interface PreviewModelsResponse {
  items: Array<{ id: string; owned_by: string; object: string }>
  count: number
}

export interface SyncModelsResponse {
  created: number
  updated: number
  total: number
}

export interface Role {
  id: string
  slug: string
  name: string
  description: string
}

export interface AdminUser {
  id: string
  phone: string
  login: string | null
  email: string | null
  first_name: string | null
  last_name: string | null
  patronymic_name: string | null
  birthday: string | null
  avatar: string | null
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  primary_role: string | null
  primary_role_slug: string | null
  last_login: string | null
}

export interface AdminUserBody {
  phone?: string
  login?: string
  email?: string
  first_name?: string
  last_name?: string
  patronymic_name?: string
  birthday?: string
  avatar?: string
  is_active?: boolean
  is_staff?: boolean
  primary_role?: string
  primary_role_slug?: string
  password?: string
}

export interface AdminUserListResponse {
  count: number
  page: number
  page_size: number
  results: AdminUser[]
}

export const adminApi = {
  listProviders: () => apiFetch<AdminProvider[]>('GET', '/api/admin/providers/'),
  createProvider: (body: Partial<AdminProvider> & { api_key?: string }) =>
    apiFetch<AdminProvider>('POST', '/api/admin/providers/', { body }),
  updateProvider: (id: string, body: Partial<AdminProvider> & { api_key?: string }) =>
    apiFetch<AdminProvider>('PATCH', '/api/admin/providers/{id}/', { body, params: { id } }),
  deleteProvider: (id: string) =>
    apiFetch<void>('DELETE', '/api/admin/providers/{id}/', { params: { id } }),
  previewModels: (id: string) =>
    apiFetch<PreviewModelsResponse>('POST', '/api/admin/providers/{id}/preview-models/', { params: { id } }),
  syncModels: (id: string, model_ids: string[], mark_active = false) =>
    apiFetch<SyncModelsResponse>('POST', '/api/admin/providers/{id}/sync-models/', {
      body: { model_ids, mark_active },
      params: { id },
    }),

  listModels: () => apiFetch<AdminModel[]>('GET', '/api/admin/models/'),
  createModel: (body: AdminModelBody) =>
    apiFetch<AdminModel>('POST', '/api/admin/models/', { body }),
  updateModel: (id: string, body: AdminModelBody) =>
    apiFetch<AdminModel>('PATCH', '/api/admin/models/{id}/', { body, params: { id } }),
  toggleModel: (id: string, is_active: boolean) =>
    apiFetch<AdminModel>('PATCH', '/api/admin/models/{id}/', { body: { is_active }, params: { id } }),
  deleteModel: (id: string) =>
    apiFetch<void>('DELETE', '/api/admin/models/{id}/', { params: { id } }),

  stats: (params?: { period?: string; date_from?: string; date_to?: string }) =>
    apiFetch<AdminStats>('GET', '/api/admin/stats/', { params }),

  storages: () => apiFetch<StorageOverview>('GET', '/api/admin/storages/'),

  logs: (params?: {
    level?: string
    source?: string
    category?: string
    task_id?: string
    row_id?: string
    search?: string
    date_from?: string
    date_to?: string
    page?: number
    page_size?: number
  }) => apiFetch<LogListResponse>('GET', '/api/admin/logs/', { params }),

  listValidators: () => apiFetch<ExternalValidatorConfig[]>('GET', '/api/admin/validators/'),
  listValidatorServices: () =>
    apiFetch<ValidatorServicesResponse>('GET', '/api/admin/validators/services/'),
  createValidator: (body: Partial<ExternalValidatorConfig> & { api_key?: string }) =>
    apiFetch<ExternalValidatorConfig>('POST', '/api/admin/validators/', { body }),
  updateValidator: (id: string, body: Partial<ExternalValidatorConfig> & { api_key?: string }) =>
    apiFetch<ExternalValidatorConfig>('PATCH', '/api/admin/validators/{id}/', { body, params: { id } }),
  deleteValidator: (id: string) =>
    apiFetch<void>('DELETE', '/api/admin/validators/{id}/', { params: { id } }),

  listUsers: (params?: { search?: string; is_active?: boolean; primary_role?: string; is_staff?: boolean; page?: number }) =>
    apiFetch<AdminUserListResponse>('GET', '/api/admin/users/', { params }),
  createUser: (body: Partial<AdminUserBody>) =>
    apiFetch<AdminUser>('POST', '/api/admin/users/', { body }),
  updateUser: (id: string, body: Partial<AdminUserBody>) =>
    apiFetch<AdminUser>('PATCH', '/api/admin/users/{id}/', { body, params: { id } }),
  deleteUser: (id: string) =>
    apiFetch<void>('DELETE', '/api/admin/users/{id}/', { params: { id } }),
  setUserPassword: (id: string, password: string) =>
    apiFetch<{ detail: string }>('POST', '/api/admin/users/{id}/set-password/', { body: { password }, params: { id } }),
  toggleUserActive: (id: string, is_active: boolean) =>
    apiFetch<AdminUser>('POST', '/api/admin/users/{id}/toggle-active/', { body: { is_active }, params: { id } }),
  listRoles: () => apiFetch<Role[]>('GET', '/api/roles/'),

  listSpamFormulas: () => apiFetch<SpamFormula[]>('GET', '/api/admin/spam-formulas/'),
  createSpamFormula: (body: Partial<SpamFormula>) =>
    apiFetch<SpamFormula>('POST', '/api/admin/spam-formulas/', { body }),
  updateSpamFormula: (id: string, body: Partial<SpamFormula>) =>
    apiFetch<SpamFormula>('PATCH', '/api/admin/spam-formulas/{id}/', { body, params: { id } }),
  saveSpamFormulaExpression: (id: string, expression: string) =>
    apiFetch<SpamFormula>('POST', '/api/admin/spam-formulas/{id}/save-expression/', {
      body: { expression },
      params: { id },
    }),
  deleteSpamFormula: (id: string) =>
    apiFetch<void>('DELETE', '/api/admin/spam-formulas/{id}/', { params: { id } }),
  testSpamFormula: (text: string, opts?: {
  expression?: string
  keywords?: string
  formula_type?: 'structured_v6' | 'custom_expression'
}) =>
  apiFetch<SpamFormulaTestResult>('POST', '/api/admin/spam-formulas/test/', {
    body: {
      text,
      expression: opts?.expression || '',
      keywords: opts?.keywords || '',
      formula_type: opts?.formula_type || 'structured_v6',
    },
  }),

getSystemSettings: () =>
  apiFetch<SystemSettings>('GET', '/api/admin/settings/'),
updateSystemSettings: (body: SystemSettingsBody) =>
  apiFetch<SystemSettings>('PATCH', '/api/admin/settings/', { body }),
}

/** --- Profile (personal cabinet) ----------------------------------------- */

export interface ProfileBody {
  first_name?: string
  last_name?: string
  patronymic_name?: string
  email?: string
  phone?: string
  birthday?: string
  avatar?: string
}

export const profileApi = {
  get: () => apiFetch<AuthUser>('GET', '/api/profile/'),
  update: (body: Partial<ProfileBody>) => apiFetch<AuthUser>('PATCH', '/api/profile/', { body }),
  changePassword: (current_password: string, new_password: string) =>
    apiFetch<{ detail: string }>('POST', '/api/profile/change-password/', { body: { current_password, new_password } }),
  uploadAvatar: (file: File) => {
    const fd = new FormData()
    fd.append('avatar', file)
    return apiFetch<{ avatar: string }>('POST', '/api/profile/avatar/', { formData: fd })
  },
}
